import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';

import { useTreatmentPlanEditable } from '../../../../../hooks/useTreatmentPlanEditable';
import Visual from './components/Visual';
import { FHButton, FDropzone } from '../../../../../../../base';
import { Themes } from '../../../../../../../../constants';
import { FORTA_WHITE } from '../../../../../../../../utils/colors';
import { assessmentActions } from '../../../../../../../../store';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from '../../../../constants/states';

import type { IAssessmentTool, IAssessmentVisual } from '../../../../../../../../utils/types';

interface AssessmentVisualsComponents {
  Visual: typeof Visual;
  FDropzone: typeof FDropzone;
  FHButton: typeof FHButton;
}

interface AssessmentVisualProps {
  assessmentTool?: IAssessmentTool;
  components?: Partial<AssessmentVisualsComponents>;
  useEditable?: typeof useTreatmentPlanEditable;
}

function AssessmentVisuals({ assessmentTool, components, useEditable = useTreatmentPlanEditable }: AssessmentVisualProps) {
  const dispatch = useDispatch();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { persistDataState } = useSelector((state: any) => state.assessments);

  const [newVisual, setNewVisual] = useState<IAssessmentVisual | null>(null);
  const [isAddingVisual, setIsAddingVisual] = useState<boolean>(false);
  const [visualCount, setVisualCount] = useState<number>(0);

  const editable = useEditable();
  const VisualComponent = components?.Visual ?? Visual;
  const FDropzoneComponent = components?.FDropzone ?? FDropzone;
  const FHButtonComponent = components?.FHButton ?? FHButton;
  const pendingPreviewUrlRef = useRef<string | null>(null);
  const { id: planId } = treatmentPlan;

  const revokePendingPreviewUrl = useCallback(() => {
    if (!pendingPreviewUrlRef.current) return;
    URL.revokeObjectURL(pendingPreviewUrlRef.current);
    pendingPreviewUrlRef.current = null;
  }, []);

  useEffect(() => revokePendingPreviewUrl, [revokePendingPreviewUrl]);

  useEffect(() => {
    if (!assessmentTool?.id || !planId) return;

    let noVisualErrorValue = 0;

    if (!assessmentTool.visuals?.length && !newVisual) {
      noVisualErrorValue = 1;
    } else {
      noVisualErrorValue = 0;
    }
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.NO_VISUAL]}-${assessmentTool.id}`]: noVisualErrorValue,
        },
      }),
    );

    if (!assessmentTool.visuals) {
      return;
    }

    if (Boolean(newVisual) && assessmentTool.visuals.length > visualCount) {
      revokePendingPreviewUrl();
      setNewVisual(null);
    }
    if (persistDataState?.[`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${assessmentTool.id}-new`]) {
      setNewVisual(
        persistDataState[`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${assessmentTool.id}-new`],
      );
    } else {
      dispatch(
        assessmentActions.updateErrorState({
          data: {
            [`${ERROR_STATES[STATE_IDS.VISUAL]}-${assessmentTool.id}-new`]: 0,
          },
        }),
      );
    }
    setVisualCount(assessmentTool.visuals.length);
    // eslint-disable
  }, [assessmentTool, newVisual, revokePendingPreviewUrl]);

  const handleSelectFiles = (files: any) => {
    const file = files[0];
    if (!file) return;

    revokePendingPreviewUrl();

    const documentUrl = file.noPreviewFile ? undefined : URL.createObjectURL(file);
    pendingPreviewUrlRef.current = documentUrl ?? null;

    setNewVisual({
      label: '',
      description: '',
      document_url: documentUrl,
      document_contents: file,
      noPreviewFile: file.noPreviewFile,
    });
  };

  const handleRemoveNewVisual = () => {
    revokePendingPreviewUrl();
    setIsAddingVisual(false);
    setNewVisual(null);
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.VISUAL]}-${assessmentTool?.id}-new`]: 0,
        },
      }),
    );
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${assessmentTool?.id}-new`]: null,
        },
      }),
    );
  };

  return (
    <Box>
      {assessmentTool?.id && (
        <>
          {assessmentTool.visuals?.map((visual) => (
            <VisualComponent key={visual.id} visual={visual} toolId={assessmentTool.id} />
          ))}
          {editable && newVisual && (
            <VisualComponent
              visual={newVisual}
              toolId={assessmentTool.id}
              onRemove={handleRemoveNewVisual}
            />
          )}
          <FDropzoneComponent
            colorTheme={Themes.Primary}
            show={isAddingVisual}
            fileNameRegEx={'^[a-zA-Z0-9_., -]*$'}
            maxSize={10}
            accept={{
              'image/png': ['.png'],
              'image/jpeg': ['.jpeg'],
              'image/jpg': ['.jpg'],
            }}
            noPreviewFiles={['.pdf']}
            onSelect={handleSelectFiles}
            onClose={() => setIsAddingVisual(false)}
          />
        </>
      )}
      {editable && assessmentTool && (
        <Box sx={{ display: 'flex', mt: 3 }}>
          <FHButtonComponent
            data-testid="addVisualButton"
            disabled={!assessmentTool.id || Boolean(newVisual)}
            colorTheme={Themes.Primary}
            onClick={() => setIsAddingVisual(true)}
            startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
            error={(!assessmentTool.visuals || assessmentTool.visuals?.length === 0) && !newVisual}
          >
            Add Visual
          </FHButtonComponent>
        </Box>
      )}
    </Box>
  );
}

export default AssessmentVisuals;
